import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Tooltip } from "@fluentui/react-components";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { useResourceBudget, useResourceCard, useResourcePerformance } from "./useResourceInsightsData";
import { defaultFiltersFor, filtersForClient, filtersForCount, filtersForProject } from "./filterModel";
import { hasPermission } from "./cardFormat";
import { performanceVerdict } from "./performanceFormat";
import OverviewTab from "./OverviewTab";
import ProjectsTab from "./ProjectsTab";
import AssignmentsTab from "./AssignmentsTab";
import InsightsHeader from "./InsightsHeader";
import InsightsTabs from "./InsightsTabs";
import styles from "./resourceInsights.module.scss";

function InsightsSkeleton() {
  return (
    <div className={styles.tabContent}>
      <Skeleton height={28} width="55%" borderRadius={8} />
      <Skeleton height={110} borderRadius={12} />
      <Skeleton height={160} borderRadius={12} />
    </div>
  );
}

/**
 * Everything the application knows about how a resource is doing over a period: who they are, how loaded they are,
 * how they perform, where they stand against the hours forecast for them, the projects they work on and the tasks
 * they are assigned. It does not know where it is shown: the host passes the period, and what a task press does.
 *
 * @param resource  who it is about, `{ id, name, image, title, email, phoneNumber, isExternal }`; the name and
 *   picture show at once, while the rest is loaded
 * @param period  the period it is about, `{ startDate, endDate }` written yyyy-MM-dd
 * @param projectFilter  a project the host is limited to, `{ id, name }`; the user can still switch to all projects
 * @param showHours  whether the figures made of hours are shown; no score depends on it
 * @param periodControl  what lets the user change the period, shown under the header; leave it out when the host owns the period
 * @param onNavigateToTask  `(task)`, called when a task is pressed; without it a task cannot be pressed
 * @param onEditTask  `(task)`, called when the pencil of a task is pressed; without it there is no pencil
 * @param onVerdictChange  `(verdict)`, told the colours of the global performance index once it is known, or null while
 *   it is not, so the host can colour what it draws around Resource Insights (the title of its panel)
 */
export default function ResourceInsights({ resource, period, projectFilter = null, showHours = true, periodControl = null, onNavigateToTask, onEditTask, onVerdictChange }) {
  // A new object with the same dates is not a new period.
  const range = useMemo(() => ({ startDate: period.startDate, endDate: period.endDate }), [period.startDate, period.endDate]);
  const defaults = useMemo(() => defaultFiltersFor(range), [range]);

  const [tab, setTab] = useState("overview");
  const [scopeToProject, setScopeToProject] = useState(true);
  const [filters, setFilters] = useState(defaults);

  // The tasks start out in the period. When the period is changed while Resource Insights is open, they follow it
  // unless the user chose a period of their own.
  const previousRange = useRef(range);
  useEffect(() => {
    const previous = previousRange.current;
    previousRange.current = range;
    if (previous === range) return;

    setFilters((current) => (current.startDate === previous.startDate && current.endDate === previous.endDate ? { ...current, startDate: range.startDate, endDate: range.endDate } : current));
  }, [range]);
  const canViewTasks = useMemo(() => hasPermission("Task.View"), []);

  const projectId = projectFilter && scopeToProject ? projectFilter.id : undefined;
  const { status, card, error, reload } = useResourceCard({
    resourceId: resource.id,
    projectId,
    startDate: range.startDate,
    endDate: range.endDate,
  });

  // The indices and the budget are asked for apart from the rest, which shows at once while they follow. They need
  // the tasks, so they are only asked for with the permission to see them.
  const performance = useResourcePerformance({
    resourceId: resource.id,
    projectId,
    startDate: range.startDate,
    endDate: range.endDate,
    enabled: canViewTasks,
  });
  const budget = useResourceBudget({ resourceId: resource.id, projectId, enabled: canViewTasks });

  // The header wears the colour of the global performance index, and the host is told so it can do the same.
  const verdict = useMemo(() => performanceVerdict(performance.performance?.global), [performance.performance]);
  useEffect(() => {
    if (!onVerdictChange) return undefined;
    onVerdictChange(verdict);
    return () => onVerdictChange(null);
  }, [verdict, onVerdictChange]);

  const identity = card?.resource ?? {
    name: resource.name,
    image: resource.image,
    title: resource.title,
    email: resource.email,
    phoneNumber: resource.phoneNumber,
    isExternal: resource.isExternal,
  };

  // A count, a callout or a project of the overview opens the tasks it stands for.
  const openTasks = useCallback(
    (count, projectOfCount = null) => {
      setFilters(filtersForCount(count, projectOfCount, defaults));
      setTab("assignments");
    },
    [defaults]
  );

  // The name of a project or of a client opens the tasks of it.
  const openProject = useCallback(
    (projectOfName) => {
      setFilters(filtersForProject(projectOfName, defaults));
      setTab("assignments");
    },
    [defaults]
  );

  const openClient = useCallback(
    (clientOfName) => {
      setFilters(filtersForClient(clientOfName, defaults));
      setTab("assignments");
    },
    [defaults]
  );

  const tabs = useMemo(
    () => [
      { value: "overview", label: "Overview" },
      { value: "projects", label: "Projects" },
      ...(canViewTasks ? [{ value: "assignments", label: "Assignments" }] : []),
    ],
    [canViewTasks]
  );

  const renderBody = () => {
    if (status === "error") {
      const isNotAvailable = error?.response?.status === 404;
      return (
        <div className={styles.errorBox}>
          <span>{isNotAvailable ? "This resource is not available to you." : "Resource Insights could not be loaded."}</span>
          {!isNotAvailable && (
            <button type="button" className={styles.linkButton} onClick={reload}>
              Try again
            </button>
          )}
        </div>
      );
    }

    if (!card) return <InsightsSkeleton />;

    switch (tab) {
      case "projects":
        return <ProjectsTab card={card} showHours={showHours} canOpenTasks={canViewTasks} onOpenTasks={openTasks} onOpenProject={openProject} onOpenClient={openClient} />;
      case "assignments":
        return (
          <AssignmentsTab
            resourceId={resource.id}
            scopeProjectId={projectId}
            projects={card.projects}
            filters={filters}
            defaults={defaults}
            onFiltersChange={setFilters}
            showHours={showHours}
            onNavigate={onNavigateToTask}
            onEdit={onEditTask}
          />
        );
      default:
        return <OverviewTab card={card} performance={canViewTasks ? performance : null} budget={canViewTasks ? budget : null} showHours={showHours} canOpenTasks={canViewTasks} onOpenTasks={openTasks} />;
    }
  };

  return (
    <div className={styles.cardRoot}>
      <InsightsHeader resourceId={resource.id} identity={identity} skills={card?.skills} verdict={verdict} />

      {periodControl}

      {projectFilter && (
        <div className={styles.scopeSwitch} role="group" aria-label="Scope of Resource Insights">
          <Tooltip content={projectFilter.name} relationship="description" positioning="above">
            <button type="button" className={`${styles.segment} ${scopeToProject ? styles.segmentActive : ""}`} onClick={() => setScopeToProject(true)}>
              {projectFilter.name}
            </button>
          </Tooltip>
          <button type="button" className={`${styles.segment} ${!scopeToProject ? styles.segmentActive : ""}`} onClick={() => setScopeToProject(false)}>
            All projects
          </button>
        </div>
      )}

      <InsightsTabs tabs={tabs} selected={tab} onSelect={setTab} />

      <div className={styles.tabBody}>{renderBody()}</div>
    </div>
  );
}
